import { Button } from '@@/ui/primitives/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@@/ui/primitives/dialog';
import { Input } from '@@/ui/primitives/input';
import { Label } from '@@/ui/primitives/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@@/ui/primitives/select';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { cn, formatUserName, getCSSVariable, normalizeLinksInHtml } from '../../lib/utils';
import { Upload, X, FileText, File, BookOpen, FileImage, FileSpreadsheet, Presentation } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useGlobal } from '@@/contexts/GlobalProvider';
import { fetchUserAttributes, signOut } from 'aws-amplify/auth';
import { createFreshdeskTicket, listFreshdeskGroups } from '@@/integrations/freshdesk';
import { toast } from 'sonner';
import { Spinner } from '@@/ui/components/Spinner';

const cxCentralGreen = getCSSVariable('--cxcentral-green');

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
  const { colorScheme } = useGlobal();
  const primaryColor = colorScheme === 'cxcentral' ? cxCentralGreen : '#6488E9';
  const primaryRgb = colorScheme === 'cxcentral' ? '14 162 167' : '100 136 233';
  const primaryDark = colorScheme === 'cxcentral' ? '#0c8a8e' : '#4F73DE';
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link']
    ]
  };

  const formats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'color', 'background', 'link'];

  return (
    <>
      <style>{`
        .rich-text-editor .ql-editor {
          min-height: 150px;
          font-size: 14px;
          line-height: 1.5;
        }
        .rich-text-editor .ql-toolbar {
          border-top: none;
          border-left: none;
          border-right: none;
          border-bottom: 1px solid #E6E6E6;
          background-color: #f9fafb;
        }
        .rich-text-editor .ql-container {
          border: none;
          font-family: inherit;
        }
        .rich-text-editor .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }

        /* Custom Theme - driven by --primary-color */

        /* Toolbar Button and Dropdown Styles - Specific targeting */
        .rich-text-editor .ql-snow.ql-toolbar button:hover,
        .rich-text-editor .ql-snow.ql-toolbar button:focus,
        .rich-text-editor .ql-snow.ql-toolbar button.ql-active,
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-label:hover,
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-label:focus,
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-label.ql-active {
          color: var(--primary-color) !important;
        }

        .rich-text-editor .ql-snow.ql-toolbar button:hover .ql-stroke,
        .rich-text-editor .ql-snow.ql-toolbar button:focus .ql-stroke,
        .rich-text-editor .ql-snow.ql-toolbar button.ql-active .ql-stroke,
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke,
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-label:focus .ql-stroke,
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke {
          stroke: var(--primary-color) !important;
        }

        .rich-text-editor .ql-snow.ql-toolbar button:hover .ql-fill,
        .rich-text-editor .ql-snow.ql-toolbar button:focus .ql-fill,
        .rich-text-editor .ql-snow.ql-toolbar button.ql-active .ql-fill,
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-label:hover .ql-fill,
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-label:focus .ql-fill,
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-fill {
          fill: var(--primary-color) !important;
        }

        /* Dropdown Items */
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-item:hover,
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-item.ql-selected {
          color: var(--primary-color) !important;
        }

        /* Dropdown Options */
        .rich-text-editor .ql-snow .ql-picker-options {
          background-color: white !important;
          border: 1px solid #E6E6E6 !important;
        }

        .rich-text-editor .ql-snow .ql-picker-options .ql-picker-item:hover {
          background-color: rgb(var(--primary-rgb) / 0.1) !important;
          color: var(--primary-color) !important;
        }


        /* Links in Editor */
        .rich-text-editor .ql-editor a {
          color: var(--primary-color) !important;
        }

        .rich-text-editor .ql-editor a:hover {
          color: var(--primary-dark) !important;
        }

        /* Selection and Cursor */
        .rich-text-editor .ql-editor ::selection {
          background-color: rgb(var(--primary-rgb) / 0.3) !important;
        }

        /* Color Picker Specific */
        .rich-text-editor .ql-snow .ql-color-picker .ql-picker-item:hover {
          border-color: var(--primary-color) !important;
        }

        /* Tooltip */
        .rich-text-editor .ql-tooltip {
          background-color: var(--primary-color) !important;
          border-color: var(--primary-color) !important;
        }

        .rich-text-editor .ql-tooltip::before {
          border-top-color: var(--primary-color) !important;
        }

        .rich-text-editor .ql-tooltip input[type=text] {
          border-color: var(--primary-color) !important;
        }

        .rich-text-editor .ql-tooltip input[type=text]:focus {
          border-color: var(--primary-dark) !important;
          box-shadow: 0 0 0 2px rgb(var(--primary-rgb) / 0.2) !important;
        }

        /* Reset default QuillJS blue colors to inherit from parent */
        .rich-text-editor .ql-snow.ql-toolbar button,
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-label {
          color: #374151 !important; /* Default gray color */
        }

        .rich-text-editor .ql-snow.ql-toolbar button .ql-stroke,
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-label .ql-stroke {
          stroke: #374151 !important; /* Default gray stroke */
        }

        .rich-text-editor .ql-snow.ql-toolbar button .ql-fill,
        .rich-text-editor .ql-snow.ql-toolbar .ql-picker-label .ql-fill {
          fill: #374151 !important; /* Default gray fill */
        }
      `}</style>
      <div
        className='border border-[#E6E6E6] rounded-lg bg-white overflow-hidden'
        style={
          {
            '--primary-color': primaryColor,
            '--primary-rgb': primaryRgb,
            '--primary-dark': primaryDark
          } as React.CSSProperties
        }
      >
        <ReactQuill
          theme='snow'
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          style={{
            minHeight: '250px',
            border: 'none'
          }}
          className='rich-text-editor'
        />
      </div>
    </>
  );
};

const FILE_TYPES = {
  images: {
    // Freshdesk documented common image types
    extensions: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif'],
    mimeTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/tiff']
  },
  pdfs: {
    extensions: ['pdf'],
    mimeTypes: ['application/pdf']
  },
  documents: {
    // Word + Rich Text + Plain Text
    extensions: ['doc', 'docx', 'rtf', 'txt'],
    mimeTypes: [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/rtf',
      'text/rtf',
      'text/plain'
    ]
  },
  spreadsheets: {
    // Excel + CSV
    extensions: ['xls', 'xlsx', 'csv'],
    mimeTypes: [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ]
  },
  presentations: {
    // PowerPoint
    extensions: ['ppt', 'pptx'],
    mimeTypes: [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ]
  }
};

// Freshdesk attachment limits vary by plan. Use 15MB/file to be safe across plans.
const MAX_FILE_SIZE = 15 * 1024 * 1024;

const validateFile = (file: File): { isValid: boolean; error?: string } => {
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File size must be less than 15MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`
    };
  }

  const extension = file.name.split('.').pop()?.toLowerCase();
  const mimeType = file.type.toLowerCase();

  // Check if file type is supported
  const allSupportedExtensions = Object.values(FILE_TYPES).flatMap((type) => type.extensions);
  const allSupportedMimeTypes = Object.values(FILE_TYPES).flatMap((type) => type.mimeTypes);

  const isExtensionSupported = extension && allSupportedExtensions.includes(extension);
  const isMimeTypeSupported = allSupportedMimeTypes.includes(mimeType);

  if (!isExtensionSupported && !isMimeTypeSupported) {
    return {
      isValid: false,
      error:
        'Unsupported file type. Supported: Images (jpg, jpeg, png, gif, bmp, tiff), PDFs, Office docs (doc, docx, xls, xlsx, ppt, pptx), Text (txt, rtf, csv).'
    };
  }

  return { isValid: true };
};

interface FilePreviewProps {
  file: File;
  onRemove: () => void;
}

const FilePreview: React.FC<FilePreviewProps> = ({ file, onRemove }) => {
  const { colorScheme } = useGlobal();
  const primaryColor = colorScheme === 'cxcentral' ? cxCentralGreen : '#6488E9';
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  React.useEffect(() => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, [file]);

  const getFileIcon = () => {
    const type = file.type.toLowerCase();
    const extension = file.name.split('.').pop()?.toLowerCase();

    // Images
    if (FILE_TYPES.images.extensions.includes(extension || '') || FILE_TYPES.images.mimeTypes.includes(type)) {
      return <FileImage className='w-8 h-8' style={{ color: primaryColor }} />;
    }
    // PDFs
    else if (FILE_TYPES.pdfs.extensions.includes(extension || '') || FILE_TYPES.pdfs.mimeTypes.includes(type)) {
      return <FileText className='w-8 h-8' style={{ color: primaryColor }} />;
    }
    // Documents (Word, Text)
    else if (
      FILE_TYPES.documents.extensions.includes(extension || '') ||
      FILE_TYPES.documents.mimeTypes.includes(type)
    ) {
      return <BookOpen className='w-8 h-8' style={{ color: primaryColor }} />;
    }
    // Spreadsheets (Excel)
    else if (
      FILE_TYPES.spreadsheets.extensions.includes(extension || '') ||
      FILE_TYPES.spreadsheets.mimeTypes.includes(type)
    ) {
      return <FileSpreadsheet className='w-8 h-8' style={{ color: primaryColor }} />;
    }
    // Presentations (PowerPoint)
    else if (
      FILE_TYPES.presentations.extensions.includes(extension || '') ||
      FILE_TYPES.presentations.mimeTypes.includes(type)
    ) {
      return <Presentation className='w-8 h-8' style={{ color: primaryColor }} />;
    }
    // Default
    else {
      return <File className='w-8 h-8' style={{ color: primaryColor }} />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className='relative group bg-white border border-dashed border-gray-100 rounded-lg overflow-hidden hover:shadow-md transition-shadow'>
      <button
        type='button'
        onClick={onRemove}
        className='absolute top-2 right-2 z-10 p-1 bg-red text-white rounded-full hover:bg-red transition-colors opacity-0 group-hover:opacity-100'
        title='Remove file'
      >
        <X size={14} />
      </button>

      <div className='p-2'>
        {file.type.startsWith('image/') && imagePreview ? (
          <div className='w-full h-24 mb-2 rounded flex items-center justify-center overflow-hidden'>
            <img src={imagePreview} alt={file.name} className='max-w-full max-h-full object-contain' />
          </div>
        ) : (
          <div className='w-full h-24 mb-2 bg-gray-50 rounded flex items-center justify-center'>{getFileIcon()}</div>
        )}

        <div className='space-y-1'>
          <p className='text-xs font-medium text-black truncate' title={file.name}>
            {file.name}
          </p>
          <p className='text-xs text-black'>{formatFileSize(file.size)}</p>
        </div>
      </div>
    </div>
  );
};

interface FileUploadProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({ files, onFilesChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { colorScheme } = useGlobal();
  const primaryColor = colorScheme === 'cxcentral' ? cxCentralGreen : '#6488E9';
  const primaryRgb = colorScheme === 'cxcentral' ? '14 162 167' : '100 136 233';

  const handleFiles = (newFiles: FileList | null) => {
    if (newFiles) {
      const fileArray = Array.from(newFiles);
      const validFiles: File[] = [];
      const errors: string[] = [];

      fileArray.forEach((file) => {
        const validation = validateFile(file);
        if (validation.isValid) {
          validFiles.push(file);
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      });

      if (validFiles.length > 0) {
        onFilesChange([...files, ...validFiles]);
      }

      setValidationErrors(errors);

      if (errors.length > 0) {
        setTimeout(() => setValidationErrors([]), 5000);
      }
    }
  };

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [files]
  );

  const acceptTypes = [
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.bmp',
    '.tif',
    '.tiff',
    '.pdf',
    '.doc',
    '.docx',
    '.rtf',
    '.txt',
    '.xls',
    '.xlsx',
    '.csv',
    '.ppt',
    '.pptx'
  ].join(',');

  return (
    <div className='space-y-4'>
      {validationErrors.length > 0 && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-3'>
          <p className='text-sm font-medium text-red-800 mb-2'>Upload Errors:</p>
          <ul className='text-xs text-red-700 space-y-1'>
            {validationErrors.map((error, index) => (
              <li key={index} className='list-disc list-inside'>
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer',
          dragOver
            ? 'bg-[rgb(var(--primary-rgb)/0.05)] border-[var(--primary-color)]'
            : 'border-gray-300 hover:border-gray-400'
        )}
        style={{ '--primary-color': primaryColor, '--primary-rgb': primaryRgb } as React.CSSProperties}
      >
        <Upload className='mx-auto h-8 w-8 text-gray-400 mb-2' />
        <p className='text-sm text-black mb-2'>
          Drag and drop files here, or{' '}
          <button type='button' className='underline text-[var(--primary-color)] hover:text-black'>
            browse
          </button>
        </p>
        <p className='text-xs text-gray-500'>
          Supports: Images (jpg, jpeg, png, gif, bmp, tiff), PDFs, Office docs (doc, docx, xls, xlsx, ppt, pptx), Text
          (txt, rtf, csv). Max 15MB each.
        </p>
        <input
          ref={fileInputRef}
          type='file'
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className='hidden'
          accept={acceptTypes}
        />
      </div>

      {files.length > 0 && (
        <div className='space-y-3'>
          <Label className='text-sm font-medium'>Uploaded Files ({files.length})</Label>
          <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-64 overflow-y-auto'>
            {files.map((file, index) => (
              <FilePreview key={`${file.name}-${index}`} file={file} onRemove={() => removeFile(index)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ContactUsModal = () => {
  const { isContactModalOpen, openContactModal, closeContactModal, colorScheme, refreshSupportTickets } = useGlobal();
  const primaryColor = colorScheme === 'cxcentral' ? cxCentralGreen : '#6488E9';
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [customerSupportGroupId, setCustomerSupportGroupId] = useState<number | undefined>(undefined);

  useEffect(() => {
    (async () => {
      const result = await fetchUserAttributes();

      if (!result || Object.keys(result).length === 0) {
        signOut();
        return;
      }

      const email = result.email || '';
      setUserEmail(email);

      const userName = email ? formatUserName(email.split('@')[0]) : '';
      setUserName(userName);

      const cidCapitalised = result['custom:company-id']
        ? result['custom:company-id']?.charAt(0).toUpperCase() + result['custom:company-id']?.slice(1)
        : '';
      setCompanyId(cidCapitalised);
    })();
  }, []);

  // Resolve Freshdesk group id for "Customer Support" once and cache it
  useEffect(() => {
    (async () => {
      try {
        const groups = await listFreshdeskGroups();
        const targetName = 'customer support';
        const match = groups.find((g) => (g?.name || '').toString().trim().toLowerCase() === targetName);
        if (match?.id) setCustomerSupportGroupId(Number(match.id));
      } catch (err) {
        console.error({
          message: 'Failed to fetch Freshdesk groups.',
          error: err instanceof Error ? err.message : 'Unknown error'
        });
      }
    })();
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    requestType: '',
    priority: '1',
    requestTitle: '',
    description: ''
  });

  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  React.useEffect(() => {
    if (isContactModalOpen) {
      setFormData((prev) => ({
        ...prev,
        name: userName || '',
        email: userEmail || '',
        organization: companyId || ''
      }));
    }
  }, [isContactModalOpen, userName, userEmail, companyId]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const getPlainTextFromHtml = (html: string): string => {
    if (!html) return '';
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      // Validate required fields before submitting
      const missingFields: string[] = [];
      if (!formData.name.trim()) missingFields.push('Name');
      if (!formData.email.trim()) missingFields.push('Email');
      if (!formData.organization.trim()) missingFields.push('Organization');
      if (!formData.requestType.trim()) missingFields.push('Request Type');
      if (!formData.requestTitle.trim()) missingFields.push('Request Title');
      if (!getPlainTextFromHtml(formData.description || '')) missingFields.push('Description');

      if (missingFields.length > 0) {
        toast.error('Missing required fields', { description: missingFields.join(', ') });
        return;
      }

      setIsSubmitting(true);
      const subject = formData.requestTitle || 'Support Request';
      const metaBlock = `
        <div style="font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; font-size: 14px; color: #111">
          <p style="margin:0 0 6px 0"><strong>Submitted by:</strong> ${formData.name || 'N/A'} (${formData.email})</p>
          ${formData.organization ? `<p style="margin:0 0 6px 0"><strong>Organization:</strong> ${formData.organization}</p>` : ''}
          ${formData.requestType ? `<p style=\"margin:0 0 6px 0\"><strong>Type:</strong> ${formData.requestType.replace(/-/g, ' ')}</p>` : ''}
          <hr style="margin:12px 0; border:none; border-top:1px solid #eee" />
        </div>
      `;
      const normalizedBody = normalizeLinksInHtml(formData.description || '');
      const descriptionHtml = `${metaBlock}${normalizedBody || '<p>No description provided.</p>'}`;

      const mappedType = formData.requestType || undefined;
      const type = mappedType;
      const selectedPriority = Number(formData.priority || '1');
      const ticket = await createFreshdeskTicket({
        requesterEmail: formData.email,
        requesterName: formData.name,
        subject,
        descriptionHtml,
        type,
        status: 2,
        priority: selectedPriority,
        attachments,
        groupId: customerSupportGroupId
      });

      toast.success('Support ticket created', { description: `Ticket #${ticket.id} created successfully.` });
      // Signal other components to refetch tickets
      refreshSupportTickets();
      resetForm();
      closeContactModal();
    } catch (error) {
      toast.error('Failed to create ticket', { description: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: userName || '',
      email: userEmail || '',
      organization: companyId || '',
      requestType: '',
      priority: '1',
      requestTitle: '',
      description: ''
    });
    setAttachments([]);
  };

  const handleClose = () => {
    resetForm();
    closeContactModal();
  };

  return (
    <Dialog open={isContactModalOpen} onOpenChange={(open) => (open ? openContactModal() : handleClose())}>
      <DialogContent
        className='sm:max-w-[900px] w-full max-h-[80vh] overflow-y-auto'
        style={{ '--primary-color': primaryColor } as React.CSSProperties}
      >
        <div className='relative z-10'>
          <DialogHeader className='flex justify-center items-center gap-0 mb-6'>
            <DialogTitle>
              <svg xmlns='http://www.w3.org/2000/svg' width='113' height='29' viewBox='0 0 93 9' fill='none'>
                <path
                  d='M1.49776 8.97404L-1.38328e-07 8.97404L0 0L5.72951 7.19995e-08C7.42085 9.32536e-08 8.26653 0.728176 8.26653 2.18453L8.26653 3.46029C8.26653 4.91664 7.42085 5.64482 5.72951 5.64482L1.49776 5.64482L1.49776 8.97404ZM1.49776 1.2845L1.49776 4.36323L5.648 4.36323C6.0465 4.36323 6.33405 4.2875 6.51065 4.13604C6.685 3.98652 6.77217 3.74088 6.77217 3.39913L6.77217 2.24569C6.77217 1.90394 6.685 1.6583 6.51065 1.50878C6.33405 1.35926 6.0465 1.2845 5.648 1.2845L1.49776 1.2845ZM15.1983 8.97404L13.704 8.97404L13.704 1.7221e-07L19.4301 2.44167e-07C21.1237 2.65449e-07 21.9705 0.728176 21.9705 2.18453L21.9705 3.20397C21.9705 4.45644 21.3478 5.17005 20.1025 5.34481L22.0724 8.97404L20.415 8.97404L18.4757 5.3885L15.1983 5.3885L15.1983 8.97404ZM15.1983 1.2845L15.1983 4.10691L19.3486 4.10691C19.7493 4.10691 20.0369 4.03118 20.2112 3.87972C20.3856 3.7302 20.4727 3.48457 20.4727 3.14281L20.4727 2.2457C20.4727 1.90394 20.3856 1.6583 20.2112 1.50878C20.0369 1.35926 19.7493 1.2845 19.3486 1.2845L15.1983 1.2845ZM27.8121 7.69245L27.8121 1.2845C27.8121 1.1078 27.8506 0.941774 27.9275 0.78643C28.0068 0.629144 28.1143 0.493218 28.2502 0.378652C28.386 0.264085 28.5457 0.17282 28.7291 0.104857C28.9102 0.0349524 29.1038 3.65731e-07 29.3098 3.6832e-07L35.2839 4.43392e-07C35.4876 4.45953e-07 35.6812 0.0349525 35.8646 0.104857C36.0458 0.172821 36.2054 0.264085 36.3435 0.378652C36.4794 0.493218 36.5858 0.629144 36.6628 0.78643C36.742 0.941775 36.7816 1.1078 36.7816 1.2845L36.7816 7.69245C36.7816 7.86721 36.742 8.03227 36.6628 8.18761C36.5858 8.3449 36.4794 8.4818 36.3435 8.5983C36.2054 8.71481 36.0458 8.80705 35.8646 8.87501C35.6812 8.94103 35.4876 8.97404 35.2839 8.97404L29.3098 8.97404C29.1038 8.97404 28.9102 8.94103 28.7291 8.87501C28.5457 8.80705 28.386 8.71481 28.2502 8.5983C28.1143 8.4818 28.0068 8.3449 27.9275 8.18761C27.8506 8.03227 27.8121 7.86721 27.8121 7.69245ZM29.3098 1.2845L29.3098 7.69245L35.2839 7.69245L35.2839 1.2845L29.3098 1.2845ZM44.3078 8.97404L42.8134 8.97404L42.8134 5.38011e-07L44.3078 5.5679e-07L50.5977 6.95845L50.5977 6.35832e-07L52.0954 6.54653e-07L52.0954 8.97404L50.5977 8.97404L44.3078 2.01559L44.3078 8.97404ZM65.7518 8.97404L58.1272 8.97404L58.1272 7.30451e-07L65.7518 8.26266e-07L65.7518 1.2845L59.6216 1.2845L59.6216 3.84768L64.8552 3.84768L64.8552 5.13219L59.6216 5.13219L59.6216 7.69245L65.7518 7.69245L65.7518 8.97404ZM73.8791 1.2845L70.4421 1.2845L70.4421 8.85205e-07L78.8105 9.90366e-07L78.8105 1.2845L75.3735 1.2845L75.3735 8.97404L73.8791 8.97404L73.8791 1.2845ZM84.8661 8.97404L83.0558 8.97404L86.8427 4.45061L83.1373 1.04474e-06L84.9374 1.06736e-06L87.7325 3.36126L90.5412 1.13778e-06L92.3514 1.16053e-06L88.6461 4.45061L92.4329 8.97404L90.6227 8.97404L87.7325 5.5254L84.8661 8.97404Z'
                  fill='#111111'
                />
              </svg>
            </DialogTitle>
            <DialogDescription className='font-mona-expanded text-3xl font-semibold text-black'>
              Support Request
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={handleSubmit}
            className='bg-[rgba(249,249,249,0.63)] border border-[#F2F2F2] px-10 py-8 rounded-xl max-w-[800px] mx-auto w-full space-y-6'
          >
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <Label htmlFor='name' className='mb-2'>
                  Name
                </Label>
                <Input
                  disabled
                  id='name'
                  className='bg-white border-[#E6E6E6] h-[40px] rounded-lg'
                  autoComplete='off'
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder='Enter your full name'
                  required
                />
              </div>
              <div>
                <Label htmlFor='email' className='mb-2'>
                  Email
                </Label>
                <Input
                  disabled
                  id='email'
                  type='email'
                  className='bg-white border-[#E6E6E6] h-[40px] rounded-lg'
                  autoComplete='off'
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder='Enter your email address'
                  required
                />
              </div>
            </div>

            <div className='grid grid-cols-1 gap-4'>
              <div>
                <Label htmlFor='organization' className='mb-2'>
                  Organization
                </Label>
                <Input
                  disabled
                  id='organization'
                  className='bg-white border-[#E6E6E6] h-[40px] rounded-lg'
                  autoComplete='off'
                  value={formData.organization}
                  onChange={(e) => handleInputChange('organization', e.target.value)}
                  placeholder='Enter your organization name'
                />
              </div>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <Label htmlFor='requestType' className='mb-2'>
                  Request Type
                </Label>
                <Select value={formData.requestType} onValueChange={(value) => handleInputChange('requestType', value)}>
                  <SelectTrigger className='bg-white border-[#E6E6E6] h-[40px] rounded-lg w-full'>
                    <SelectValue placeholder='Select request type' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='Bug Report'>Bug Report</SelectItem>
                    <SelectItem value='Feature Request'>Feature Request</SelectItem>
                    <SelectItem value='Technical Support'>Technical Support</SelectItem>
                    <SelectItem value='Account Issue'>Account Issue</SelectItem>
                    <SelectItem value='Other'>Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor='priority' className='mb-2'>
                  Priority
                </Label>
                <Select value={formData.priority} onValueChange={(value) => handleInputChange('priority', value)}>
                  <SelectTrigger className='bg-white border-[#E6E6E6] h-[40px] rounded-lg w-full'>
                    <SelectValue placeholder='Select priority' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='1'>Low</SelectItem>
                    <SelectItem value='2'>Medium</SelectItem>
                    <SelectItem value='3'>High</SelectItem>
                    <SelectItem value='4'>Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor='requestTitle' className='mb-2'>
                Request Title
              </Label>
              <Input
                id='requestTitle'
                className='bg-white border-[#E6E6E6] h-[40px] rounded-lg'
                autoComplete='off'
                value={formData.requestTitle}
                onChange={(e) => handleInputChange('requestTitle', e.target.value)}
                placeholder='Enter a brief title for your request'
                required
              />
            </div>

            <div>
              <Label htmlFor='description' className='mb-2'>
                Description
              </Label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => handleInputChange('description', value)}
                placeholder='Provide a detailed description of your request...'
              />
            </div>

            <div>
              <Label className='mb-2'>Attachments</Label>
              <FileUpload files={attachments} onFilesChange={setAttachments} />
            </div>

            <DialogFooter className='flex flex-col sm:flex-row gap-3 max-w-[800px] mx-auto w-full mt-8'>
              <Button type='button' variant='outline' className='rounded-lg' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type='submit'
                className={cn(
                  'rounded-lg text-white',
                  'bg-[var(--primary-color)] hover:bg-[var(--primary-color)]/80',
                  isSubmitting && 'opacity-50 cursor-not-allowed'
                )}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner className='w-4 h-4 mr-2' /> Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactUsModal;
